import { createReducer, on } from '@ngrx/store';
import * as AdvancedSearchActions from './advanced-search.actions';
import {clearPeople, clearSearchResults} from "./advanced-search.actions";

export interface AdvancedSearchState {
  companies: any[] | null; // List of companies (null when no data yet, [] when search returned no results)
  companiesResponse: any; // Full API response with pagination data
  people: any[],
  peopleResults: any, // This will store the entire apollo_result
  loading: boolean;
  error: any;
  totalRecords?: number;
  currentPage: number;
  pageSize: number;
  selectedCompany: any;
  searchFilters: any;
  // The exact built API payload ({ and: [...] }) used for the current search.
  // Reused verbatim when paginating so page N's query matches page 1's query
  // (Veridion pagination tokens are cursors bound to the original query).
  searchPayload: any;
  filterTypes: any[];
  nextPageToken: string | null;
  previousPageToken: string | null;

  // Location data stored by parent ID
  countriesList: any[];
  regionsByCountry: { [countryId: string]: any[] };
  citiesByRegion: { [regionId: string]: any[] };

  // Loading states
  loadingCountries: boolean;
  loadingRegions: { [countryId: string]: boolean };
  loadingCities: { [regionId: string]: boolean };

  // Error states
  countriesError: any;
  regionsError: { [countryId: string]: any };
  citiesError: { [regionId: string]: any };

  // Legacy location fields (for backward compatibility)
  locations: any;
  loadingLocations: boolean;
  locationError: any;
  countries: any;
  regions: any;
  cities: any;
  countryError: any;
  regionError: any;
  cityError: any;

  // Company industries
  companyIndustries: any[];
  loadingCompanyIndustries: boolean;
  companyIndustriesError: any;

  // Saved filters
  savedFilters: any;

  isNewSearch: boolean;
}

export const initialState: AdvancedSearchState = {
  companies: null, // Changed from [] to null to distinguish "no data yet" from "search returned no results"
  companiesResponse: null,
  people: [],
  peopleResults: null,
  loading: false,
  error: null,
  totalRecords: 0,
  currentPage: 1,
  pageSize: 10,
  selectedCompany: null,
  searchFilters: null,
  searchPayload: null,
  filterTypes: [],
  nextPageToken: null,
  previousPageToken: null,

  // Location data
  countriesList: [],
  regionsByCountry: {},
  citiesByRegion: {},

  loadingCountries: false,
  loadingRegions: {},
  loadingCities: {},

  countriesError: null,
  regionsError: {},
  citiesError: {},

  // Legacy fields
  locations: { data: [] },
  loadingLocations: false,
  locationError: null,
  countries: { data: [] },
  regions: { data: [] },
  cities: { data: [] },
  countryError: null,
  regionError: null,
  cityError: null,

  companyIndustries: [],
  loadingCompanyIndustries: false,
  companyIndustriesError: null,

  savedFilters: null,

  isNewSearch: false
};

export const advancedSearchReducer = createReducer(
  initialState,

  // Company search
  on(AdvancedSearchActions.searchCompanies, (state, { filters, nextPageToken }) => ({
    ...state,
    loading: true,  // This should already be true
    error: null,
    // Capture the exact page-1 payload so pagination can replay it verbatim.
    // Pagination dispatches carry a nextPageToken; for those we keep the
    // already-stored page-1 payload instead of overwriting it.
    searchPayload: nextPageToken ? state.searchPayload : filters
  })),

  on(AdvancedSearchActions.searchCompaniesSuccess, (state, { response }) => {
    return {
      ...state,
      companies: response.data.result || [],
      companiesResponse: response.data,
      totalRecords: response.data.count || 0,
      nextPageToken: response.data.pagination?.next || null,
      previousPageToken: response.data.pagination?.previous || null,
      loading: false
    };
  }),

  on(AdvancedSearchActions.searchCompaniesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
    companies: [], // Keep as [] here - search failed but we tried, so show "no results"
    companiesResponse: null,
    totalRecords: 0,
    nextPageToken: null
  })),

  // People search
  on(AdvancedSearchActions.searchPeople, (state, action) => {
    const params = (action as any).params;
    return {
      ...state,
      loading: true, // This should be true
      error: null,
      selectedCompany: params.selectedCompany
    };
  }),

  on(AdvancedSearchActions.searchPeopleSuccess, (state, { response }) => ({
    ...state,
    peopleResults: response.data.apollo_result,
    loading: false
  })),

  on(AdvancedSearchActions.searchPeopleFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
    people: []
  })),

  // Filters
  on(AdvancedSearchActions.saveSearchFilters, (state, { filters, filterTypes }) => {
    // Save to localStorage if available
    if (typeof localStorage !== 'undefined') {
      try {
        // Enhanced filter data with location information
        const enhancedFilters = filters.filters.map((filter: any) => {
          if (filter.filterType === 10 && filter.value) {
            // For location filters, ensure we save the complete data
            const enhancedFilter = { ...filter };

            // Find and save the actual location data objects
            if (filter.value.country && filter.value.countryData) {
              enhancedFilter.value = {
                ...filter.value,
                countryData: filter.value.countryData
              };
            }

            if (filter.value.region && filter.value.regionData) {
              enhancedFilter.value = {
                ...enhancedFilter.value,
                regionData: filter.value.regionData
              };
            }

            if (filter.value.city && filter.value.cityData) {
              enhancedFilter.value = {
                ...enhancedFilter.value,
                cityData: filter.value.cityData
              };
            }

            return enhancedFilter;
          }
          return filter;
        });

        localStorage.setItem('advancedSearchFilters', JSON.stringify({
          filters: enhancedFilters,
          filterTypes
        }));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
    }

    return {
      ...state,
      searchFilters: filters,
      filterTypes: filterTypes,
      savedFilters: {
        filters: filters.filters,
        filterTypes
      }
    };
  }),

  on(AdvancedSearchActions.loadSavedFilters, (state) => {
    let savedFilters = null;

    if (typeof localStorage !== 'undefined') {
      const savedJson = localStorage.getItem('advancedSearchFilters');
      if (savedJson) {
        try {
          savedFilters = JSON.parse(savedJson);
        } catch (error) {
          console.error('Error parsing saved filters:', error);
        }
      }
    }

    return {
      ...state,
      savedFilters,
      searchFilters: savedFilters?.filters || null,
      filterTypes: savedFilters?.filterTypes || []
    };
  }),

  on(AdvancedSearchActions.clearSavedFilters, (state) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('advancedSearchFilters');
    }

    return {
      ...state,
      searchFilters: null,
      filterTypes: [],
      savedFilters: null
    };
  }),

  // Clear actions
  on(clearPeople, (state) => ({
    ...state,
    people: [],
    peopleResults: null,
    loading: true, // SET TO TRUE, not false
    selectedCompany: state.selectedCompany // Keep the selected company
  })),

  on(clearSearchResults, (state) => ({
    ...state,
    companies: null, // Changed from [] to null - clear means back to initial state
    companiesResponse: null,
    totalRecords: 0,
    nextPageToken: null,
    previousPageToken: null,
    currentPage: 1,
    loading: true,  // IMPORTANT: Set to true
    error: null
  })),

  on(AdvancedSearchActions.setNewSearchFlag, (state, { isNewSearch }) => ({
    ...state,
    isNewSearch
  })),

  // NEW Location Actions - Store-based approach
  on(AdvancedSearchActions.loadCountries, (state) => ({
    ...state,
    loadingCountries: true,
    countriesError: null
  })),

  on(AdvancedSearchActions.loadCountriesSuccess, (state, { countries }) => ({
    ...state,
    countriesList: countries,
    loadingCountries: false
  })),

  on(AdvancedSearchActions.loadCountriesFailure, (state, { error }) => ({
    ...state,
    countriesError: error,
    loadingCountries: false
  })),

  // Regions
  on(AdvancedSearchActions.loadRegions, (state, { countryId }) => ({
    ...state,
    loadingRegions: { ...state.loadingRegions, [countryId]: true },
    regionsError: { ...state.regionsError, [countryId]: null }
  })),

  on(AdvancedSearchActions.loadRegionsSuccess, (state, { countryId, regions }) => ({
    ...state,
    regionsByCountry: { ...state.regionsByCountry, [countryId]: regions },
    loadingRegions: { ...state.loadingRegions, [countryId]: false }
  })),

  on(AdvancedSearchActions.loadRegionsFailure, (state, { error }) => ({
    ...state,
    regionsError: error,
    loadingRegions: {}
  })),

  // Cities
  on(AdvancedSearchActions.loadCities, (state, { regionId }) => ({
    ...state,
    loadingCities: { ...state.loadingCities, [regionId]: true },
    citiesError: { ...state.citiesError, [regionId]: null }
  })),

  on(AdvancedSearchActions.loadCitiesSuccess, (state, { regionId, cities }) => ({
    ...state,
    citiesByRegion: { ...state.citiesByRegion, [regionId]: cities },
    loadingCities: { ...state.loadingCities, [regionId]: false }
  })),

  on(AdvancedSearchActions.loadCitiesFailure, (state, { error }) => ({
    ...state,
    citiesError: error,
    loadingCities: {}
  })),

  // Company Industries
  on(AdvancedSearchActions.searchCompanyIndustries, (state) => ({
    ...state,
    loadingCompanyIndustries: true,
    companyIndustriesError: null
  })),

  on(AdvancedSearchActions.searchCompanyIndustriesSuccess, (state, { response }) => ({
    ...state,
    companyIndustries: response.data || [],
    loadingCompanyIndustries: false
  })),

  on(AdvancedSearchActions.searchCompanyIndustriesFailure, (state, { error }) => ({
    ...state,
    loadingCompanyIndustries: false,
    companyIndustriesError: error
  })),

  // Legacy location actions (for backward compatibility)
  on(AdvancedSearchActions.searchCountries, (state) => ({
    ...state,
    loadingCountries: true,
    countryError: null
  })),

  on(AdvancedSearchActions.searchCountriesSuccess, (state, { response }) => ({
    ...state,
    countries: response,
    loadingCountries: false
  })),

  on(AdvancedSearchActions.searchCountriesFailure, (state, { error }) => ({
    ...state,
    countryError: error,
    loadingCountries: false
  })),

  on(AdvancedSearchActions.searchRegions, (state) => ({
    ...state,
    loadingRegions: { ...state.loadingRegions },
    regionError: null
  })),

  on(AdvancedSearchActions.searchRegionsSuccess, (state, { response }) => ({
    ...state,
    regions: response,
    loadingRegions: {}
  })),

  on(AdvancedSearchActions.searchRegionsFailure, (state, { error }) => ({
    ...state,
    regionError: error,
    loadingRegions: {}
  })),

  on(AdvancedSearchActions.searchCities, (state) => ({
    ...state,
    loadingCities: { ...state.loadingCities },
    cityError: null
  })),

  on(AdvancedSearchActions.searchCitiesSuccess, (state, { response }) => ({
    ...state,
    cities: response,
    loadingCities: {}
  })),

  on(AdvancedSearchActions.searchCitiesFailure, (state, { error }) => ({
    ...state,
    cityError: error,
    loadingCities: {}
  })),

  on(AdvancedSearchActions.startSearch, (state) => ({
    ...state,
    companies: null, // Changed from [] to null - starting search means we don't have data yet
    companiesResponse: null,
    totalRecords: 0,
    nextPageToken: null,
    previousPageToken: null,
    currentPage: 1,
    loading: true,  // Set loading to true
    error: null
  }))
);
